import { useContext, useState, useRef, useEffect } from "react"
import axios from "axios"
import { Calendar } from "primereact/calendar"
import { InputText } from "primereact/inputtext"
import { Dropdown } from "primereact/dropdown"
import { InputNumber } from "primereact/inputnumber"
import { Accordion, AccordionTab } from "primereact/accordion"
import { Button } from "primereact/button"
import { Toast } from "primereact/toast"
import PhoneField from "../Utils/PhoneField"
import { classNames } from "primereact/utils"
import EditorField from "../Utils/EditorField"
import EndJobForm from "./EndJobForm"
import RadioButtonField from "../Utils/RadioButtonField"
import styles from "../../../styles/Participant-Employment.module.css"
import { UserContext } from "../../../context/userContext"
import { countries, states } from "../Types/countries"
import { validateEmail } from "../Utils/Utils"
import {
  Actions,
  showError,
  formatDateAndDuration,
  convertToLocalDateTime,
  requieredFieldIsMissing,
  renderRequireFieldErrorMessage,
  addOneMinute,
  substractOneMinute,
  formatDate,
} from "../Utils/Utils"
import { PayRates, YesNo, EngagementChannels } from "../Types/Dropdowns"

import { employmentApiUrl } from "../../../config/microservicesUrls"

const api_url = employmentApiUrl()

export default function JobRecord({
  closeForm,
  action,
  jobRecord,
  jobSearchPlanId,
  updateData,
}) {
  const toast = useRef(null)

  const userCtx = useContext(UserContext)

  const [loading, setLoading] = useState(false)

  const [formErrors, setFormErrors] = useState(null)

  const [showEndJobForm, setShowEndJobForm] = useState(false)

  const USA = "US"

  const defaultData = {
    engagementDate: formatDate(new Date()),
    engagementChannel: EngagementChannels[0],
    startTime: new Date(),
    endTime: addOneMinute(new Date()),
    firstEmploymentDay: undefined,
    jobTitle: "",
    duties: "",
    rateOfPay: undefined,
    pay: 0.0,
    benefits: "",
    isUnionPosition: undefined,
    workSchedule: "",
    disclosureToContactEmployer: undefined,
    employerName: "",
    employerCountry: USA,
    employerAddressOne: "",
    employerAddressTwo: "",
    employerCity: "",
    employerState: "",
    employerZipCode: undefined,
    supervisorName: "",
    supervisorEmail: "",
    supervisorPhone: "",
  }

  const [data, setData] = useState(defaultData)

  useEffect(() => {
    if (jobRecord !== null) {
      const engagementDate = convertToLocalDateTime(jobRecord.engagementDate)
      const startTime = convertToLocalDateTime(jobRecord.startTime)
      const endTime = convertToLocalDateTime(jobRecord.endTime)
      const firstEmploymentDay =
        jobRecord.firstEmploymentDay != null
          ? convertToLocalDateTime(jobRecord.firstEmploymentDay)
          : null

      const { formattedDate, formattedStartTime, formattedEndTime } =
        formatDateAndDuration(engagementDate, startTime, endTime)

      const isUnionPosition = jobRecord.isUnionPositionStr
      const disclosureToContactEmployer =
        jobRecord.disclosureToContactEmployerStr
      setData({
        ...jobRecord,
        engagementDate: formattedDate,
        engagementChannel: EngagementChannels[jobRecord.engagementChannel],
        startTime: formattedStartTime,
        endTime: formattedEndTime,
        firstEmploymentDay: firstEmploymentDay,
        isUnionPosition,
        disclosureToContactEmployer,
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const clearForm = () => {
    setData(defaultData)
    setFormErrors(null)
  }

  const handleSave = async () => {
    setLoading(true)
    const engagementChannel = EngagementChannels.indexOf(data.engagementChannel)
    const { formattedDate, formattedStartTime, formattedEndTime } =
      formatDateAndDuration(data.engagementDate, data.startTime, data.endTime)

    const rateOfPay = data.rateOfPay
      ? PayRates.indexOf(data.rateOfPay)
      : undefined
    const isUnionPosition = data.isUnionPosition
      ? YesNo.indexOf(data.isUnionPosition)
      : undefined
    const disclosureToContactEmployer = data.disclosureToContactEmployer
      ? YesNo.indexOf(data.disclosureToContactEmployer)
      : undefined

    if (action === Actions.Add) {
      const createdData = {
        createdBy: userCtx.session.user.staff_id,
        ...data,
        engagementChannel,
        engagementDate: formattedDate,
        startTime: formattedStartTime,
        endTime: formattedEndTime,
        rateOfPay,
        isUnionPosition,
        disclosureToContactEmployer,
        jobSearchPlanId,
      }
      axios
        .post(`${api_url}/jobRecord`, createdData, {
          headers: { Authorization: `Bearer ${userCtx.tokenEmployment}` },
        })
        .then(function (response) {
          clearForm()
          updateData(response.data)
          setLoading(false)
          closeForm()
        })
        .catch(function (response) {
          const errors = response["response"]["data"]["errors"]
          setLoading(false)
          setFormErrors(errors)
          showError(toast, "Not able to create job record", errors)
        })
    } else {
      const updatedData = {
        ...data,
        updatedBy: userCtx.session.user.staff_id,
        engagementChannel,
        engagementDate: formattedDate,
        startTime: formattedStartTime,
        endTime: formattedEndTime,
        rateOfPay,
        isUnionPosition,
        disclosureToContactEmployer,
        jobSearchPlanId,
      }
      axios
        .put(`${api_url}/jobRecord`, updatedData, {
          headers: { Authorization: `Bearer ${userCtx.tokenEmployment}` },
        })
        .then(function (response) {
          clearForm()
          updateData(response.data)
          setLoading(false)
          closeForm()
        })
        .catch(function (response) {
          const errors = response["response"]["data"]["errors"]
          setLoading(false)
          setFormErrors(errors)
          showError(toast, "Not able to update job record", errors)
        })
    }
  }

  const renderFormErrors = () => {
    if (formErrors !== null) {
      return (
        <ul className={styles.errors_list}>
          {formErrors.map((error, index) => {
            return (
              <li key={index}>
                <small className="p-error">{error}</small>
              </li>
            )
          })}
        </ul>
      )
    }
    return null
  }
  return (
    <>
      <Toast ref={toast} position="top-right" />
      {!showEndJobForm &&
        jobRecord &&
        !jobRecord.ended &&
        action !== Actions.View && (
          <Button
            label="End Job"
            disabled={loading}
            className={styles.top_right_btn}
            onClick={() => {
              setShowEndJobForm(true)
            }}
          />
        )}
      {!showEndJobForm && (
        <div className="main-content">
          <div style={{ display: "flex", justifyContent: "space-between" }}>
            <h1 className={styles.view_title}>
              {action === Actions.Add
                ? "Add job record"
                : action === Actions.Edit
                  ? "Edit job record"
                  : "Job record"}
            </h1>
            <Button
              className={styles.rounded_btn}
              icon="pi pi-times"
              type="button"
              onClick={() => {
                clearForm()
                closeForm()
              }}
            />
          </div>
          <div className={"card flex flex-wrap p-fluid " + styles.wrapper}>
            <h3 className={styles.view_subtitle}>Job record</h3>
            <hr className={styles.line}></hr>
            <div className={styles.flex_card}>
              <div className={styles.field}>
                <label className={`${styles.label} ${styles.required}`}>
                  Engagement date
                </label>
                <Calendar
                  name="Engagement date"
                  value={data.engagementDate}
                  panelClassName="custom_calendar_panel"
                  onChange={(e) =>
                    setData({ ...data, engagementDate: e.value })
                  }
                  dateFormat="mm/dd/yy"
                  showIcon
                  className={`${styles.calendar} ${requieredFieldIsMissing(data.engagementDate)
                    ? styles.invalid
                    : ""
                    }`}
                  disabled={action === Actions.View}
                />
                {renderRequireFieldErrorMessage(
                  data.engagementDate,
                  "Engagement date is required"
                )}
              </div>
              <div className={styles.field} style={{ flexGrow: "1" }}>
                <label className={`${styles.label} ${styles.required}`}>
                  Engagement channel
                </label>
                <Dropdown
                  value={data.engagementChannel}
                  options={EngagementChannels}
                  panelClassName="custom_dropdown_panel"
                  onChange={(e) =>
                    setData({ ...data, engagementChannel: e.value })
                  }
                  placeholder="Select a channel"
                  style={{ height: "32px", alignItems: "center" }}
                  disabled={action === Actions.View}
                />
              </div>
              <div className={styles.field}>
                <label className={`${styles.label} ${styles.required}`}>
                  Start time
                </label>
                <Calendar
                  name="Start time"
                  value={data.startTime}
                  timeOnly
                  panelClassName="custom_calendar_panel"
                  onChange={(e) => setData({ ...data, startTime: e.value })}
                  showIcon
                  icon="pi pi-clock"
                  className={`${styles.calendar} ${requieredFieldIsMissing(data.startTime)
                    ? styles.invalid
                    : ""
                    }`}
                  hourFormat="12"
                  maxDate={substractOneMinute(data.endTime)}
                  disabled={action === Actions.View}
                />
                {renderRequireFieldErrorMessage(
                  data.startTime,
                  "Start time is required"
                )}
              </div>
              <div className={styles.field}>
                <label className={`${styles.label} ${styles.required}`}>
                  End time
                </label>
                <Calendar
                  name="End time"
                  value={data.endTime}
                  timeOnly
                  panelClassName="custom_calendar_panel"
                  hourFormat="12"
                  onChange={(e) => setData({ ...data, endTime: e.value })}
                  showIcon
                  icon="pi pi-clock"
                  className={`${styles.calendar} ${requieredFieldIsMissing(data.endTime) ? styles.invalid : ""
                    }`}
                  minDate={addOneMinute(data.startTime)}
                  disabled={action === Actions.View}
                />
                {renderRequireFieldErrorMessage(
                  data.endTime,
                  "End time is required"
                )}
              </div>
            </div>

            <div className={styles.flex_card}>
              <div className={styles.field}>
                <label className={styles.label}>First day of employment</label>
                <Calendar
                  name="First day of employment"
                  value={data.firstEmploymentDay}
                  panelClassName="custom_calendar_panel"
                  onChange={(e) =>
                    setData({ ...data, firstEmploymentDay: e.value })
                  }
                  dateFormat="mm/dd/yy"
                  showIcon
                  className={styles.calendar}
                  disabled={action === Actions.View}
                />
              </div>
              <div className={styles.field} style={{ flexGrow: "1" }}>
                <label className={styles.label}>Job title</label>
                <InputText
                  value={data.jobTitle}
                  onChange={(e) =>
                    setData({ ...data, jobTitle: e.target.value })
                  }
                  className={"p-inputtext-md"}
                  disabled={action === Actions.View}
                  name="jobTitle"
                />
              </div>
            </div>

            <EditorField
              value={data.duties}
              onTextChange={(e) => setData({ ...data, duties: e })}
              label="Duties"
              disabled={action === Actions.View}
            />

            <div className={styles.field}>
              <label className={styles.label}>Rate of pay</label>
              <div
                style={{ display: "flex", gap: "10px", alignItems: "flex-end" }}
              >
                <Dropdown
                  value={data.rateOfPay}
                  options={PayRates}
                  panelClassName="custom_dropdown_panel"
                  onChange={(e) => setData({ ...data, rateOfPay: e.value })}
                  placeholder="Select a value"
                  style={{ height: "32px", alignItems: "center" }}
                  disabled={action === Actions.View}
                />
                <InputNumber
                  className={styles.pay}
                  value={data.pay}
                  onValueChange={(e) => setData({ ...data, pay: e.value })}
                  maxFractionDigits={2}
                  min={0}
                  mode="currency"
                  currency="USD"
                  locale="en-US"
                  disabled={action === Actions.View}
                  name="pay"
                />
              </div>
            </div>

            <EditorField
              value={data.benefits}
              onTextChange={(e) => setData({ ...data, benefits: e })}
              label="Benefits"
              disabled={action === Actions.View}
            />

            <div className={styles.field} style={{ width: "178px" }}>
              <RadioButtonField
                label="Is this a job union position?"
                value={data.isUnionPosition}
                setValue={(e) => setData({ ...data, isUnionPosition: e })}
                short
                disabled={action === Actions.View}
              />
            </div>

            <EditorField
              value={data.workSchedule}
              onTextChange={(e) => setData({ ...data, workSchedule: e })}
              label="Work schedule"
              disabled={action === Actions.View}
            />
            <div className={styles.field} style={{ width: "202px" }}>
              <RadioButtonField
                label="Disclosure to contact employer"
                value={data.disclosureToContactEmployer}
                setValue={(e) =>
                  setData({ ...data, disclosureToContactEmployer: e })
                }
                short
                disabled={action === Actions.View}
              />
            </div>

            <div className={styles.field}>
              <Accordion className="custom_accordion">
                <AccordionTab header="Employer">
                  <div className={styles.field} style={{ width: "100%" }}>
                    <label className={styles.label}>Name</label>
                    <InputText
                      value={data.employerName}
                      onChange={(e) =>
                        setData({ ...data, employerName: e.target.value })
                      }
                      className={"p-inputtext-md"}
                      disabled={action === Actions.View}
                      name="employerName"
                    />
                  </div>
                  <div
                    className="card flex flex-wrap p-fluid"
                    style={{ gap: "15px", width: "100%" }}
                  >
                    <div className={styles.field} style={{ flexGrow: "3" }}>
                      <label className={styles.label}>Country</label>
                      <Dropdown
                        style={{
                          marginTop: "0px",
                          height: "32px",
                          alignItems: "center",
                        }}
                        value={data.employerCountry}
                        options={countries}
                        panelClassName="custom_dropdown_panel"
                        optionLabel="name"
                        optionValue="code"
                        filter
                        showClear
                        onChange={(e) =>
                          setData({ ...data, employerCountry: e.value })
                        }
                        placeholder="Select a country"
                        disabled={action === Actions.View}
                      />
                    </div>
                    {data.employerCountry === USA && (
                      <div className={styles.field} style={{ flexGrow: "3" }}>
                        <label className={styles.label}>State</label>
                        <Dropdown
                          style={{
                            marginTop: "0px",
                            height: "32px",
                            alignItems: "center",
                          }}
                          value={data.employerState}
                          options={states}
                          optionLabel="name"
                          optionValue="code"
                          panelClassName="custom_dropdown_panel"
                          filter
                          showClear
                          onChange={(e) =>
                            setData({ ...data, employerState: e.value })
                          }
                          placeholder="Select a state"
                          disabled={action === Actions.View}
                        />
                      </div>
                    )}
                    <div className={styles.field} style={{ flexGrow: "3" }}>
                      <label className={styles.label}>City</label>
                      <InputText
                        value={data.employerCity}
                        onChange={(e) =>
                          setData({ ...data, employerCity: e.target.value })
                        }
                        className={"p-inputtext-md"}
                        disabled={action === Actions.View}
                        name="employerCity"
                      />
                    </div>
                    <div className={styles.field} style={{ flexGrow: "1" }}>
                      <label className={styles.label}>Zip code</label>
                      <InputText
                        style={{ width: "100px" }}
                        className={"p-inputtext-md"}
                        keyfilter="int"
                        maxLength={5}
                        value={data.employerZipCode}
                        onChange={(e) =>
                          setData({ ...data, employerZipCode: e.target.value })
                        }
                        disabled={action === Actions.View}
                        name="employerZipCode"
                      />
                    </div>
                    <div className={styles.field} style={{ width: "100%" }}>
                      <label className={styles.label}>Street address</label>
                      <InputText
                        value={data.employerAddressOne}
                        onChange={(e) =>
                          setData({
                            ...data,
                            employerAddressOne: e.target.value,
                          })
                        }
                        className={"p-inputtext-md"}
                        disabled={action === Actions.View}
                        name="employerAddressOne"
                      />
                      <InputText
                        style={{ marginTop: "10px" }}
                        value={data.employerAddressTwo}
                        onChange={(e) =>
                          setData({
                            ...data,
                            employerAddressTwo: e.target.value,
                          })
                        }
                        className={"p-inputtext-md"}
                        disabled={action === Actions.View}
                        name="employerAddressTwo"
                      />
                    </div>
                  </div>
                </AccordionTab>
              </Accordion>
            </div>

            <div className={styles.field}>
              <Accordion className="custom_accordion">
                <AccordionTab header="Supervisor">
                  <div className={styles.field}>
                    <label className={styles.label}>Name</label>
                    <InputText
                      className={"p-inputtext-md"}
                      value={data.supervisorName}
                      onChange={(e) =>
                        setData({ ...data, supervisorName: e.target.value })
                      }
                      disabled={action === Actions.View}
                      name="supervisorName"
                    />
                  </div>
                  <div className={styles.field}>
                    <label className={styles.label}>Email</label>
                    <InputText
                      value={data.supervisorEmail}
                      className={classNames({
                        "p-inputtext-md": true,
                        "p-invalid":
                          data.supervisorEmail !== null &&
                          data.supervisorEmail.length > 0 &&
                          !validateEmail(data.supervisorEmail),
                      })}
                      onChange={(e) => {
                        setData({ ...data, supervisorEmail: e.target.value })
                      }}
                      disabled={action === Actions.View}
                      name="supervisorEmail"
                    />
                    {data.supervisorEmail.length > 0 &&
                      !validateEmail(data.supervisorEmail) && (
                        <small className="p-error">Invalid email address</small>
                      )}
                  </div>
                  <div className={styles.field}>
                    <PhoneField
                      label="Phone"
                      className={"p-inputtext-md"}
                      value={data.supervisorPhone}
                      setValue={(value) =>
                        setData({ ...data, supervisorPhone: value })
                      }
                      disabled={action === Actions.View}
                    />
                  </div>
                </AccordionTab>
              </Accordion>
            </div>
            {jobRecord && jobRecord.ended && (
              <Accordion className="custom_accordion">
                <AccordionTab header="Job end details">
                  <EndJobForm
                    jobRecord={data}
                    action={action}
                    updateData={(d) => setData({ ...data, ...d })}
                  />
                </AccordionTab>
              </Accordion>
            )}
            <br />
            <div
              style={{
                display: "flex",
                gap: "20px",
                justifyContent: "flex-end",
              }}
            >
              <Button
                className={styles.cancel_button}
                type="button"
                disabled={loading}
                onClick={() => {
                  clearForm()
                  closeForm()
                }}
              >
                {action === Actions.View ? "Back" : "Cancel"}
              </Button>
              {action !== Actions.View && (
                <Button
                  className={styles.button}
                  type="submit"
                  disabled={
                    requieredFieldIsMissing(data.engagementDate) ||
                    requieredFieldIsMissing(data.startTime) ||
                    requieredFieldIsMissing(data.endTime) ||
                    requieredFieldIsMissing(data.engagementChannel) ||
                    loading
                  }
                  icon={loading ? "pi pi-spin pi-spinner" : ""}
                  iconPos="right"
                  label={`${action === Actions.Add ? "Create" : "Update"
                    } job record`}
                  onClick={() => handleSave()}
                />
              )}
            </div>
            {renderFormErrors()}
          </div>
        </div>
      )}
      {showEndJobForm && (
        <EndJobForm
          closeForm={() => setShowEndJobForm(false)}
          jobRecord={jobRecord}
          action={action}
          updateData={(data) => updateData(data)}
        />
      )}
    </>
  )
}
